import type { ReportMeta, ReportSection } from "./types";

export const DEFAULT_YEAR = "2018";
export const DEFAULT_QUARTER = "Q1";
export const DEFAULT_MONTH = "Meskerem";
export const DEFAULT_REGION_TYPE = "national";
export const DEFAULT_SELECTED_REGION = "Addis Ababa";
export const DEFAULT_REPORT_LANGUAGE = "en";
export const DEFAULT_EXPORT_FORMAT = "docx";
export const DEFAULT_USER_INSTRUCTION =
  "Generate a concise health performance report using ...";

export const CHART_COLORS = [
  "#2563eb", "#10b981", "#f59e0b", "#ef4444",
  "#8b5cf6", "#06b6d4", "#f97316",
];

export const LINE_CHART_COLOR = "#2563eb";

export const PERCENT_LABEL_REGEX =
  /(percent|%|coverage|rate|positivity|achievement|share|proportion)/i;

export const SAMPLE_REPORT_META: ReportMeta = {
  title: "National Immunization & Maternal Health Performance",
  reportType: "Annual Performance Report",
  geography: "Ethiopia (National)",
  timePeriod: "2018 E.C.",
};

export const SAMPLE_REPORT_SECTIONS: ReportSection[] = [
  {
    id: "sample-executive",
    title: "Executive Summary",
    content:
      "National full immunization coverage reached 72% in 2018 E.C., with measurable gains in Penta 3 and measles first-dose indicators. Maternal health performance shows steady ANC first-visit uptake; ANC 4+ coverage and skilled delivery remain priority areas for regional acceleration.",
    isStreaming: false,
  },
  {
    id: "sample-immunization",
    title: "Immunization Coverage by Region",
    content:
      "Regional performance varies: urban and high-density zones exceed 80% full immunization, while pastoralist and remote woredas need intensified outreach and cold-chain support.",
    chartType: "bar",
    chartData: [
      { name: "Addis Ababa", value: 85 },
      { name: "Oromia", value: 78 },
      { name: "Amhara", value: 74 },
      { name: "SNNPR", value: 71 },
      { name: "Tigray", value: 69 },
      { name: "Somali", value: 58 },
    ],
    chartMetricLabel: "Full immunization (%)",
    isStreaming: false,
  },
  {
    id: "sample-anc",
    title: "ANC & Maternal Care Trends",
    content:
      "ANC first-visit coverage improved consistently over five years. ANC 4+ visits and skilled birth attendance track below national targets in several regions—recommended focus for the next reporting cycle.",
    chartType: "line",
    chartData: [
      { name: "2014", value: 58 },
      { name: "2015", value: 62 },
      { name: "2016", value: 66 },
      { name: "2017", value: 70 },
      { name: "2018", value: 74 },
    ],
    chartMetricLabel: "ANC 4+ coverage (%)",
    isStreaming: false,
  },
  {
    id: "sample-mix",
    title: "Key Indicator Mix",
    content:
      "Share of reported maternal and child health workload by program area—immunization sessions dominate facility reporting volume, followed by ANC contacts.",
    chartType: "pie",
    chartData: [
      { name: "Immunization", value: 42 },
      { name: "ANC", value: 28 },
      { name: "Skilled delivery", value: 18 },
      { name: "PNC", value: 12 },
    ],
    chartMetricLabel: "Share (%)",
    isStreaming: false,
  },
];

export const MOCK_CHART_DATA = [
  { name: "Jan", value: 400 },
  { name: "Feb", value: 300 },
  { name: "Mar", value: 600 },
  { name: "Apr", value: 800 },
  { name: "May", value: 500 },
  { name: "Jun", value: 700 },
];

export const MOCK_PIE_DATA = [
  { name: "Group A", value: 400 },
  { name: "Group B", value: 300 },
  { name: "Group C", value: 300 },
  { name: "Group D", value: 200 },
];

export const TEMPLATES = [
  { id: "yearly" as const, label: "Annual Report", icon: "FileText" },
  { id: "quarterly" as const, label: "Quarterly Report", icon: "Calendar" },
  { id: "monthly" as const, label: "Monthly Report", icon: "Clock" },
];

// Kept in step with the region list the query planner is given in
// backend/core/prompts.py so a regional report can be requested for any known region.
export const REGIONS = [
  "Addis Ababa", "Afar", "Amhara", "Benishangul-Gumuz", "Central Ethiopia", "Dire Dawa",
  "Gambela", "Harari", "Oromia", "Sidama", "Somali", "South West Ethiopia",
  "South Ethiopia", "Tigray", 
];

export const QUARTERS = ["Q1", "Q2", "Q3", "Q4"];

// The health data source records periods by their Ethiopian month name
// (see backend/core/prompts.py), so periods are selected and stated in that calendar.
export const MONTHS = [
  "Meskerem", "Tikimt", "Hidar", "Tahsas", "Tir", "Yekatit",
  "Megabit", "Miyazia", "Ginbot", "Sene", "Hamle", "Nehase",
];

export const MONTHS_AMHARIC: Record<string, string> = {
  Meskerem: "መስከረም",
  Tikimt: "ጥቅምት",
  Hidar: "ኅዳር",
  Tahsas: "ታኅሣሥ",
  Tir: "ጥር",
  Yekatit: "የካቲት",
  Megabit: "መጋቢት",
  Miyazia: "ሚያዝያ",
  Ginbot: "ግንቦት",
  Sene: "ሰኔ",
  Hamle: "ሐምሌ",
  Nehase: "ነሐሴ",
};

export const MONTH_GREGORIAN_EQUIVALENTS: Record<string, string> = {
  Meskerem: "September",
  Tikimt: "October",
  Hidar: "November",
  Tahsas: "December",
  Tir: "January",
  Yekatit: "February",
  Megabit: "March",
  Miyazia: "April",
  Ginbot: "May",
  Sene: "June",
  Hamle: "July",
  Nehase: "August",
};

const MONTH_BY_GREGORIAN: Record<string, string> = Object.fromEntries(
  Object.entries(MONTH_GREGORIAN_EQUIVALENTS).map(([ethiopian, gregorian]) => [
    gregorian.toLowerCase(),
    ethiopian,
  ]),
);

// Accepts alternate transliterations and Gregorian wording so periods written by an
// earlier version, or by the model, still resolve to a single canonical month.
export const MONTH_ALIASES: Record<string, string> = {
  miazia: "Miyazia",
  myazia: "Miyazia",
  gimbot: "Ginbot",
  jinjot: "Ginbot",
  ...MONTH_BY_GREGORIAN,
};

// Every spelling that resolves to a canonical month.
export const MONTH_TOKENS = Array.from(
  new Set([...MONTHS, ...Object.keys(MONTH_ALIASES)]),
).sort((a, b) => b.length - a.length);

export const YEARS = Array.from(
  { length: parseInt(DEFAULT_YEAR) - 2000 + 1 },
  (_, i) => String(parseInt(DEFAULT_YEAR) - i),
);

export const PPTX_THEME = {
  ink: "0F172A",
  muted: "64748B",
  faint: "E2E8F0",
  panel: "F8FAFC",
  cyan: "0E7490",
  cyanDark: "155E75",
  emerald: "059669",
  amber: "D97706",
  white: "FFFFFF",
};

export const NUMBER_PREFIX_REGEX = [
  /^\s*section\s+\d+[\.\)\-:]?\s*/i,
  /^\s*(?:\d+|[ivxlcdm]+)[\.\)\-:]\s*/i,
  /^\s*\d+\s+/,
];
